import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import SettingsRecommendationsDynamicRateRangesTdDiscount from 'lumen/components/settings/recommendations/dynamic-rate-ranges/td-discount';
import type { RecommendationStrategy } from 'lumen/models';
import type { DynamicRateDiscount } from 'lumen/models/recommendation-strategy';
import { toStoredValue } from 'lumen/utils/pricing-assistant/value-conversion';

type DynamicRateRangesRow = {
  maximum: DynamicRateDiscount | null;
  minimum: DynamicRateDiscount | null;
  // The raw rate code (used for write-back and per-cell test ids)
  rate: string;
  // What the Rate column shows: the user defined rate code names, or the raw code as fallback.
  rateName: string;
};

interface SettingsRecommendationsDynamicRateRangesStrategyTableSignature {
  Args: {
    isEditMode: boolean;
    saveAttempted: boolean;
    strategy: RecommendationStrategy;
  };
}

export default class SettingsRecommendationsDynamicRateRangesStrategyTable extends Component<SettingsRecommendationsDynamicRateRangesStrategyTableSignature> {
  @service declare intl: Services['intl'];

  onDiscountChange = (
    rate: string,
    setting: 'maximum' | 'minimum',
    value: number | null,
  ): void => {
    const { strategy } = this.args;

    if (
      !strategy ||
      strategy.settings.settings_type !== 'dynamic_rate_discount_range'
    ) {
      return;
    }

    const { settings } = strategy;

    // Preserve the discount type the BE sent; newly-set fields default to relative
    // (the only type supported for now). Relative percentages are stored as fractions.
    const type = settings.rate_discounts[rate]?.[setting]?.type ?? 'relative';

    const discount: DynamicRateDiscount | null =
      value === null
        ? null
        : {
            type,
            value: toStoredValue(value, type),
          };

    strategy.settings = {
      ...settings,
      rate_discounts: {
        ...settings.rate_discounts,
        [rate]: {
          ...(settings.rate_discounts[rate] ?? {
            maximum: null,
            minimum: null,
          }),
          [setting]: discount,
        },
      },
    };
  };

  get columns(): CustomEmberTableColumn[] {
    const { isEditMode, saveAttempted, strategy } = this.args;

    return [
      {
        isEditing: isEditMode,
        name: this.intl.t(
          'pricing_assistant.recommendations.settings.dynamic_rate_ranges.table.rate',
        ),
        thClassNames: 'text-left',
        valuePath: 'rateName',
      },
      {
        isEditing: isEditMode,
        name: this.intl.t(
          'pricing_assistant.recommendations.settings.dynamic_rate_ranges.table.minimum',
        ),
        onDiscountChange: this.onDiscountChange,
        saveAttempted,
        strategy,
        tdComponent: SettingsRecommendationsDynamicRateRangesTdDiscount,
        thClassNames: 'text-left',
        valuePath: 'minimum',
      },
      {
        isEditing: isEditMode,
        name: this.intl.t(
          'pricing_assistant.recommendations.settings.dynamic_rate_ranges.table.maximum',
        ),
        onDiscountChange: this.onDiscountChange,
        saveAttempted,
        strategy,
        tdComponent: SettingsRecommendationsDynamicRateRangesTdDiscount,
        thClassNames: 'text-left',
        valuePath: 'maximum',
      },
    ];
  }

  get rows(): DynamicRateRangesRow[] {
    const { settings } = this.args.strategy;

    if (settings.settings_type !== 'dynamic_rate_discount_range') {
      return [];
    }

    return Object.entries(settings.rate_discounts ?? {}).map(
      ([rate, { maximum, minimum, user_defined_rate_code_name }]) => ({
        maximum,
        minimum,
        rate,
        rateName: user_defined_rate_code_name ?? rate,
      }),
    );
  }

  <template>
    <PrismTable @columns={{this.columns}} @rows={{this.rows}} />
  </template>
}
